import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@shared/store';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useWorkspaceScope } from './useWorkspaceScope';
import {
  useAcknowledgeWorkspaceReviewMutation,
  type RuntimeBudgetUsage,
  type WorkspaceOperation,
} from './workspaceApi';
export const budgetCredits = (units: string | null, scale: number | null) => {
  if (
    units == null ||
    scale == null ||
    !Number.isSafeInteger(scale) ||
    scale < 0 ||
    scale > 9 ||
    !/^\d+$/.test(units)
  )
    return 'Unavailable';
  if (!scale) return units;
  const s = units.padStart(scale + 1, '0');
  return `${s.slice(0, -scale)}.${s.slice(-scale)}`;
};
export const RuntimeBudgetPanel = ({ operation }: { operation: WorkspaceOperation }) => {
  const scope = useWorkspaceScope();
  return <BudgetSession key={`${scope}:${operation.id}`} operation={operation} />;
};
const BudgetSession = ({ operation }: { operation: WorkspaceOperation }) => {
  const user = useAppSelector((s) => s.auth.authenticatedUser),
    me = useUserMeControllerUserMeQuery();
  const workspace = useDesignWorkspace();
  const [ack, { isLoading }] = useAcknowledgeWorkspaceReviewMutation();
  const [open, setOpen] = useState(false),
    [error, setError] = useState(''),
    [receipt, setReceipt] = useState<RuntimeBudgetUsage | null>(null);
  const id = useRef(crypto.randomUUID()),
    alive = useRef(true),
    busy = useRef(false);
  const runtime = receipt ?? operation.runtimeBudget;
  const canManage =
    !!user &&
    user.role !== 'viewer' &&
    !me.isError &&
    me.currentData?.id === user.id &&
    me.currentData.companyId === user.companyId &&
    me.currentData.role !== 'viewer' &&
    me.currentData.privileges.includes('campaign.run');
  const ready =
    !!runtime &&
    runtime.state === 'review' &&
    runtime.commandsFinished &&
    runtime.financialState !== 'review' &&
    [
      runtime.activeCalls,
      runtime.pendingCalls,
      runtime.unsettledCalls,
      runtime.unknownDispatches,
      runtime.pendingDispatches,
    ].every((v) => v === 0);
  const allowed = useRef(false);
  allowed.current = canManage && ready && !workspace.underlyingDirty;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  if (!runtime) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={1.5}>
        <Typography variant="subtitle2">Runtime budget · {runtime.state}</Typography>
        <Typography variant="body2">
          Limits: credits {runtime.limits.credits ?? 'Unlimited'} · tool invocations{' '}
          {runtime.limits.tools ?? 'Unlimited'} · duration{' '}
          {runtime.limits.seconds == null ? 'Unlimited' : `${runtime.limits.seconds} seconds`}
        </Typography>
        <Typography variant="body2">
          Financial evidence: {runtime.financialState}
          {runtime.exhaustionReason
            ? ` · Admission stopped: ${runtime.exhaustionReason.replaceAll('_', ' ')}`
            : ''}
        </Typography>
        {(['spentUnits', 'accruedUnits', 'heldUnits', 'overrunUnits'] as const).map((key) => (
          <Typography key={key} variant="body2">
            {
              {
                spentUnits: 'Settled credits',
                accruedUnits: 'Accrued credits',
                heldUnits: 'Held credits',
                overrunUnits: 'Recorded overrun',
              }[key]
            }
            : {budgetCredits(runtime[key], runtime.creditScale)}
          </Typography>
        ))}
        <Typography variant="body2">
          Tool invocations: {runtime.toolInvocations} · Active calls: {runtime.activeCalls} ·
          Pending: {runtime.pendingCalls} · Unsettled: {runtime.unsettledCalls} · Unknown
          dispatches: {runtime.unknownDispatches} · Pending dispatches: {runtime.pendingDispatches}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Limits stop new actions; active calls may finish. Overrun stays in the original run.
          Review acknowledgement preserves the recorded outcome and does not resume the mission.
        </Typography>
        {runtime.acknowledgedAt ? (
          <Alert severity="info">
            Review acknowledged {new Date(runtime.acknowledgedAt).toLocaleString()}. The original
            operation remains {operation.state}.
          </Alert>
        ) : (
          runtime.state === 'review' && (
            <>
              <Button
                disabled={!canManage || !ready || isLoading || workspace.underlyingDirty}
                onClick={() => setOpen(true)}>
                Acknowledge completed review
              </Button>
              <Typography variant="caption">
                Requires finished commands, zero active/pending/unsettled calls and unknown
                dispatches, reconciled finances and no outstanding admitted/started permits. The
                server rechecks all evidence.
              </Typography>
            </>
          )
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Dialog
          open={open}
          onClose={() => {
            if (!busy.current) setOpen(false);
          }}>
          <DialogTitle>Acknowledge completed review?</DialogTitle>
          <DialogContent>
            This releases the reviewed run only after server reconciliation. It does not declare an
            uncertain effect successful, change original evidence or resume the paused mission.
          </DialogContent>
          <DialogActions>
            <Button disabled={isLoading} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!canManage || !ready || isLoading || workspace.underlyingDirty}
              onClick={async () => {
                if (!alive.current || busy.current || !allowed.current) return;
                busy.current = true;
                setError('');
                try {
                  const result = await ack({ id: operation.id, requestId: id.current }).unwrap();
                  if (alive.current) {
                    setReceipt(result);
                    setOpen(false);
                  }
                } catch {
                  if (alive.current)
                    setError(
                      'Acknowledgement was not confirmed. Inspect outstanding evidence and retry with the same request.',
                    );
                } finally {
                  busy.current = false;
                }
              }}>
              Confirm acknowledgement
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Paper>
  );
};
